import { useMemo, useState } from "react";
import { ptBR } from "date-fns/locale";
import { eachDayOfInterval, endOfMonth, format, startOfMonth } from "date-fns";
import { empty } from "@exact-magic/utils";
import type { Box, Boxes } from "@sdr/api/chat/schedule/types";
import { Calendar } from "../../ui/calendar";
import { Button } from "../../ui/button";
import { useChat } from "../../context/use-chat";

type ScheduleModalProps = {
  boxes: Boxes;
  onClose: () => void;
};

const getDateKey = (date: Date) => format(date, "yyyy-MM-dd");

const ScheduleModal = ({ boxes, onClose }: ScheduleModalProps) => {
  const today = new Date();
  const { append } = useChat();
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [box, setBox] = useState<Box | undefined>(undefined);
  const [month, setMonth] = useState<Date>(today);
  const available = useMemo(
    () => (date ? boxes[getDateKey(date)] || [] : []),
    [boxes, date],
  );
  const disabled = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfMonth(month),
        end: endOfMonth(month),
      }).filter((date) => empty(boxes[getDateKey(date)])),
    [boxes, month],
  );

  const last = Object.values(boxes)
    .flatMap((box) => box)
    .map((box) => new Date(box.end))
    .reduce((last, box) => (box > last ? box : last), today);

  const handleDateSelect = (date: Date | undefined) => {
    setDate(date);
    setBox(undefined);
  };

  const handleBoxSelect = (box: Box) => {
    setBox(box);
  };

  const handleConfirm = () => {
    if (date && box) {
      append(
        `Agende para o dia ${format(box.start, "d 'de' MMMM 'às' HH:mm", {
          locale: ptBR,
        })}h`,
        { box },
      );
    }
  };

  const handleBack = () => {
    setDate(undefined);
    setBox(undefined);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 flex-grow overflow-hidden flex flex-col">
        <h2 className="text-lg font-semibold mb-4">
          {date
            ? `Selecione o melhor horário em ${format(date, "dd/MM/yyyy")}`
            : "Agende uma reunião"}
        </h2>
        {!date ? (
          <div className="flex-grow flex items-center justify-center">
            <Calendar
              mode="single"
              selected={date}
              onMonthChange={setMonth}
              disabled={[...disabled, { before: today }, { after: last }]}
              onSelect={handleDateSelect}
            />
          </div>
        ) : (
          <div className="flex-grow rounded-md border">
            <div className="p-4">
              {available.map((b) => (
                <Button
                  key={b.id!}
                  variant={box === b ? "default" : "outline"}
                  onClick={() => handleBoxSelect(b)}
                  className="w-full mb-2 justify-between"
                >
                  <span>{format(b.start, "HH:mm")}</span>
                  <span>-</span>
                  <span>{format(b.end, "HH:mm")}</span>
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="p-4 border-t flex justify-between">
        <Button variant="outline" onClick={date ? handleBack : onClose}>
          {date ? "Voltar" : "Cancelar"}
        </Button>
        <Button onClick={handleConfirm} disabled={!date || !box}>
          {date ? "Confirmar" : "Próximo"}
        </Button>
      </div>
    </div>
  );
};

export type SchedulePromptProps = {
  boxes: Boxes;
};

export const SchedulePrompt = ({ boxes }: SchedulePromptProps) => {
  const { append } = useChat();
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  return (
    <div className="px-4 py-3">
      <div className="inline-flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => {
            setShowScheduleModal(true);
          }}
          className="flex-grow"
        >
          Sim
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            append("Não");
          }}
          className="flex-grow"
        >
          Não
        </Button>
        {showScheduleModal && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-lg w-full h-full max-w-[280px] max-h-[480px]">
              <ScheduleModal
                boxes={boxes}
                onClose={() => setShowScheduleModal(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
